// CarteGesteDemande.jsx — CE QU'IL PRÉPARE QUAND DIMITRI LE DEMANDE.
//
// ══ POURQUOI UNE SECONDE CARTE, ET PAS UN ÉLARGISSEMENT DE L'AUTRE ══════════
//
// `CarteGeste.jsx` rend une PROPOSITION née en base : elle porte « ce qu'il a
// vu », un motif FIGÉ composé de faits vérifiés, et elle se réclame avant de
// partir pour qu'un second onglet ne la refasse pas.
//
// Ici, rien de tout ça n'existe. Dimitri vient de DIRE « change le prix de
// Cocon Naturel à 40 € » : il n'y a pas de fait observé à raconter (le fait,
// c'est sa phrase), pas de ligne à réclamer, et le motif — quand le levier en
// exige un — c'est LUI qui l'écrit, sur-le-champ.
//
// ⚠️ Fondre les deux aurait produit une carte pleine de « si » : un motif tantôt
// figé tantôt éditable sur le même écran est exactement le genre de nuance
// qu'on finit par lire de travers, un soir, sur un geste qui compte.
//
// ══ CE QUE LA CARTE DOIT DIRE, ET DANS CET ORDRE ═══════════════════════════
//
// Dimitri, 2026-09-09 : « ma règle "il propose, je clique" suffit à me
// protéger. Elle protège même mieux qu'une interdiction, parce que JE VOIS CE
// QUI VA SE PASSER AVANT QUE ÇA SE PASSE. »
//
//   1. LE TITRE — du REGISTRE, jamais du modèle. Un modèle qui rédigerait son
//      libellé pourrait annoncer autre chose que ce que l'action fait, et le
//      clic vaudrait consentement à la phrase, pas au fait.
//   2. LES ARGUMENTS, en toutes lettres. C'est le seul endroit où « 40 € » se
//      lit avant de partir.
//   3. LES AVERTISSEMENTS (montant, sortie de chez FBZ, suppression) — AVANT le
//      bouton, jamais après.
//   4. CE QU'IL VERRA APRÈS — du registre également.
//
// ⚠️ ET L'EXÉCUTION PASSE PAR `executerGeste`, la mécanique qui existe déjà :
// elle relit le registre à l'instant du clic, refiltre les arguments, envoie le
// jeton de DIMITRI, et surtout distingue les trois natures d'échec — dont
// « on ne sait pas si c'est parti », qu'un fetch écrit à la va-vite oublie.

import {
  useState,
  useRef,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import { DS } from "../../lib/ui";
import { supabase } from "../../supabase";
import {
  executerGeste,
  MAX_CARS_TEXTE,
  ENDPOINTS_AUTORISES,
  _refusDeVague,
  valeurSaisie,
} from "../../lib/geste-executer";
// Le registre dit quels arguments sont du TEXTE (`args_textes`) — la carte le
// relit, comme `executerGeste`, plutôt que de faire confiance à ce qu'elle porte.
// Et ce qu'un geste ne peut pas OMETTRE (`args_indispensables`) : le même juge
// que le serveur et que le clic, relu ici sur les arguments portés.
import {
  lireLevier,
  manqueIndispensable,
  saisieSApplique,
  ARGS_LIBELLES,
  motsDuBooleen,
} from "../../lib/operateur-leviers";
// L'aperçu que le SERVEUR a déjà lu pour composer sa phrase (« cette nuit est
// déjà vendue ») : la carte le reprend au lieu de relire — même état, même
// jeton, un aller-retour de moins. Fail-closed : `null` ⇒ on relit.
import { apercuPrelu } from "../../lib/apercu-prelu";
// ⚠️ LA COMPOSITION DES DEUX ÉNONCÉS EST PURE, ET ELLE VIT AILLEURS
// (`src/lib/carte-dite.js`). Ici on ne fait que LUI DONNER les valeurs que la
// carte a déjà dérivées pour les afficher — jamais de recalcul, sinon ce qui
// se lit et ce qui s'affiche divergeraient en silence.
import { phraseDeLaCarte } from "../../lib/carte-dite";

// ⚠️ RECOPIÉE de `lib/geste-resume.js::MAX_CARS_RESUME` — CRA/webpack refuse
// les imports hors de `src/`, et ce module-là est requis côté serveur par les
// handlers. Même procédé que `MAX_CARS_TEXTE` dans `src/lib/geste-executer.js` :
// la constante est recopiée, et l'ÉGALITÉ est verrouillée par un test
// (`src/views/operateur/CarteGesteDemande.resume.test.jsx`). Le serveur borne
// déjà ; celle-ci est la seconde barrière, pour qu'une réponse forgée ne
// remplisse pas la carte.
const MAX_CARS_RESUME = 900;

const BORD = "#e3e8ee";

// Ce qu'un marquage DIT à Dimitri, en une ligne, avant le bouton.
//
// ⚠️ `sortant` N'EST PLUS UNE FORMULE GÉNÉRALE (2026-09-15). Elle disait
// « plateformes, voyageur ou boîte mail » — une énumération de tout ce qui
// pouvait arriver, donc rien sur ce qui allait arriver LÀ.
//
// ⚠️ ET IL NE RÉPÈTE PAS LA DESTINATION (revue adverse #1440, correction 9).
// Une première version la recopiait ici (« Ça sort de chez vous : Beds24,
// donc Airbnb et Booking ») ET dans « Où ça part » juste dessous — la même
// phrase deux fois, ce que Dimitri refuse (« jamais deux fois le même
// texte »). Le marquage se réduit à l'icône et au fait ; la destination
// réelle, glosée par le serveur, ne vit QUE dans « Où ça part ».
const AVERTISSEMENTS = {
  argent: "💶 Ce geste porte un montant — relisez-le.",
  supprime: "🗑️ Ça supprime — ce qui part ne revient pas tout seul.",
  sortant: "📤 Ça sort de chez vous.",
};

/**
 * Un argument, rendu lisible. Un objet devient « clé : valeur », jamais du
 * JSON — et un booléen devient « oui »/« non », jamais `true`/`false`
 * (prod du 22/09 : « une activation ou une désactivation · true »).
 */
export function lisible(v) {
  if (v == null) return "—";
  if (typeof v === "boolean") return v ? "oui" : "non";
  // Un booléen venu du modèle EN CHAÎNE (`"true"`) se lit pareil — sinon il
  // s'affichait tel quel (revue, mineur e).
  if (v === "true" || v === "false") return v === "true" ? "oui" : "non";
  if (typeof v === "object" && !Array.isArray(v))
    return Object.entries(v)
      .map(([k, x]) => `${k} : ${lisible(x)}`)
      .join(" · ");
  return String(v);
}

// ══ LES ARGUMENTS, DITS — JAMAIS LEURS NOMS DE COLONNES ════════════════════
//
// Mesuré en prod le 2026-09-22 : la carte d'un geste affichait
// « logement_id lc1 · champ commission_taux · nouvelle 20 ». Trois défauts
// dans une seule ligne, et Dimitri les a tous les trois nommés ailleurs :
//
//   1. `lc1` est un code de base de données (`lib/operateur-noms.js` :
//      « il me dit lc1, lc3, l8 … qu'ils ne sortent jamais à l'écran »).
//   2. `logement_id`, `commission_taux` sont des noms de champs — le
//      glossaire `ARGS_LIBELLES` existe précisément pour les dire.
//   3. Et un identifiant n'a RIEN à faire à l'écran, même juste : ce qu'il
//      désigne se dit par son nom, pas par sa clé primaire.
//
// ⚠️ ET QUAND LE NOM MANQUE, ON N'AFFICHE RIEN. Pas `lc1` en repli : une
// ligne absente se remarque et se corrige ; un code affiché, lui, se lit
// comme une donnée et s'oublie. Le nom vient de l'APERÇU, lu au serveur avec
// le jeton de Dimitri — jamais d'une table de logements que le navigateur
// n'a pas, et jamais du repère prononcé : `vise` ne sort pas vers l'écran
// (`lib/reperes-geste.js::_sansVise`), c'est une garde, pas un oubli.

// ⚠️ LES VALEURS AUSSI SONT DES CODES, PARFOIS. « un canal : tchat_ota » a le
// libellé traité et la valeur restée technique. Les listes fermées du registre
// en portent d'autres (`a_racheter`, `charge_fixe`, `manual_pinned`…) : elles
// touchent au métier (ménage, compta, livret), leur formulation est une
// décision de Dimitri, pas une invention d'ici. On ne dit donc que ce qui est
// tranché — et cette table est le seul endroit où en ajouter.
const VALEURS_LIBELLES = {
  canal: {
    tchat_ota: "le tchat de la plateforme",
    email: "l'e-mail",
    sms: "le SMS",
    whatsapp: "WhatsApp",
  },
  // Les trois marquages d'un lieu d'alentours (`livret_alentours_poi_toggle`) :
  // à côté, `value` dit « Poser / Retirer le marquage » (`motsDuBooleen`).
  flag: {
    manual_pinned: "épinglé à la main",
    manual_excluded: "exclu à la main",
    displayed_to_guest: "montré au voyageur",
  },
  // Le verdict d'une dégradation (lot 3 des repères) : l'écran Incidents dit
  // « Non récupérable », la carte le dit pareil. La VOIX, elle, dira
  // « irrécupérable » (`carte-dite.js`, `DITS_A_LA_VOIX`) — « non » est une
  // rétractation quand le micro l'entend.
  verdict: {
    recuperable: "récupérable",
    non_recuperable: "non récupérable",
  },
};

/** La valeur d'un argument, dite — par sa table de libellés si elle en a une. */
function valeurDite(cle, v) {
  const ferme = VALEURS_LIBELLES[cle] && VALEURS_LIBELLES[cle][v];
  return ferme || moisEnClair(jourEnClair(lisible(v)));
}

const JOURS_FR = [
  "dimanche",
  "lundi",
  "mardi",
  "mercredi",
  "jeudi",
  "vendredi",
  "samedi",
];
const MOIS_FR = [
  "janvier",
  "février",
  "mars",
  "avril",
  "mai",
  "juin",
  "juillet",
  "août",
  "septembre",
  "octobre",
  "novembre",
  "décembre",
];

/**
 * « 2026-09-26 » → « samedi 26 septembre 2026 ». Le reste, inchangé.
 *
 * ⚠️ JUMEAU DE `lib/date-reperes.js::libelleJour`, recopié ici parce que
 * CRA/webpack refuse les imports hors de `src/` (même procédé que
 * `MAX_CARS_TEXTE`). Douze lignes sans dépendance, et un test les compare aux
 * dates que la carte rend : une date ISO sur une carte, c'est le format de la
 * base qui remonte à l'écran.
 */
/**
 * « 2026-09 » → « septembre 2026 ». Le reste, inchangé.
 *
 * ⚠️ UNE CLÉ DE MOIS N'EST PAS UN MOIS (lot 6, mesuré au DOM). La carte du net
 * hôte rendait « un mois : 2026-09 » — le format de la base, sur le seul écran
 * lu avant un « oui » d'argent. Dimitri lit « Septembre 2026 » sur sa facture ;
 * `2026-09` lui demande de traduire, et il cliquera quand même.
 *
 * ⚠️ JUMEAU de `lib/format.js::formatMois`, recopié ici pour la même raison
 * que `jourEnClair` : CRA/webpack refuse les imports hors de `src/`.
 */
export function moisEnClair(v) {
  const t = String(v == null ? "" : v);
  if (!/^\d{4}-\d{2}$/.test(t)) return t;
  const [a, m] = t.split("-").map(Number);
  if (m < 1 || m > 12) return t;
  return `${MOIS_FR[m - 1]} ${a}`;
}

export function jourEnClair(v) {
  const t = String(v == null ? "" : v);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(t)) return t;
  const [a, m, j] = t.split("-").map(Number);
  if (m < 1 || m > 12 || j < 1 || j > 31) return t;
  const d = new Date(Date.UTC(a, m - 1, j));
  if (Number.isNaN(d.getTime())) return t;
  return `${JOURS_FR[d.getUTCDay()]} ${j === 1 ? "1er" : j} ${MOIS_FR[m - 1]} ${a}`;
}

// ⚠️ LES IDENTIFIANTS QUI NE FINISSENT PAS PAR `_id` (lot 6, mesuré au DOM).
// `code` EST la référence de plateforme d'une réservation — « HMQKTF3F5D ».
// Elle ne finit pas par `_id`, donc elle s'affichait : la carte du net hôte
// rendait « une ligne du mois : HMQKTF3F5D » sur le chemin de la facture.
// Ce qu'elle désigne est porté par l'EN-TÊTE DE L'APERÇU, qui le lit au
// serveur — comme pour `run_id` et `incident_id`. Liste FERMÉE : on nomme, on
// ne devine pas par la forme (un code de promo, lui, se lit et se vérifie).
const CLES_IDENTIFIANTES = ["code"];

/** Une clé qui désigne une ligne en base — `*_id`, `*_ids`, `threadId`, `code`… */
export function estIdentifiant(cle) {
  return (
    /(^|_)ids?$/i.test(cle) ||
    /Ids?$/.test(cle) ||
    CLES_IDENTIFIANTES.includes(cle)
  );
}

// ══ UN ARGUMENT PEUT ÊTRE UN CODE SANS QUE SA CLÉ LE DISE ══════════════════
//
// ⚠️ MESURÉ EN PROD LE 23/09 : la carte d'un réglage de message affichait
// « un gabarit · caution_reminder_j2 ». La clé (`slug`) n'est pas un
// identifiant au sens d'`estIdentifiant` — elle n'en a pas la forme — et
// pourtant le registre le dit noir sur blanc (`lib/leviers/messages.js`) :
// « le slug ne se prononce pas — et le modèle ne l'a jamais vu ». Il n'avait
// rien à faire sous les yeux de Dimitri non plus.
//
// ⚠️ ET LA FORME DE LA VALEUR NE PEUT PAS TRANCHER — C'EST MESURÉ, PAS
// SUPPOSÉ. La première version regardait le CODE (`[a-z0-9]+_[a-z0-9_]+`) :
// balayée sur le registre entier, elle effaçait `a_racheter` (le verdict d'un
// incident), `charge_fixe` et `depense_variable` (la sorte d'une ligne de
// compta), `medecin_proche`, `non_remboursable`, `guide_appareils` — cinquante
// valeurs MÉTIER qui se lisent très bien et que Dimitri doit voir avant de
// cliquer. Le tiret bas n'est pas la frontière entre un code et un mot.
//
// La frontière, c'est le REGISTRE : `args_resolus` déclare exactement les
// arguments que Dimitri ne prononce jamais et que le serveur retrouve pour
// lui. Sauf quand le même argument porte AUSSI une liste fermée
// (`args_valeurs`) : là c'est un vocabulaire métier, pas un identifiant de
// ligne — `key` vaut `non_remboursable` sur une politique d'annulation, et
// `medecin_proche` sur un contact de santé.
// ⚠️ ET UN SEUL DE CES ARGUMENTS EST L'OBJET DU GESTE (revue adverse M5).
// `livret_alentours_poi_toggle` en résout SIX depuis le même lieu — `key`,
// `place_id`, `tisseo_url`, `manual_id`… — et recopier le nom lu sur tous
// rendait « un lien Tisséo · Le Bibent » : un nom juste, sur une ligne qui
// désigne autre chose. Le nom lu ne vaut que pour le PREMIER résolu déclaré
// (l'objet du geste, celui que l'aperçu est allé lire) ; les autres sont des
// rouages du même objet et se taisent.
// Les seules clés d'identifiant qu'on sait remplacer par un NOM : celui que la
// lecture d'aperçu a rendu. `target_logement_id` EST le logement de cette
// lecture (« copier VERS ») ; `source_logement_id` a son propre nom, que la
// lecture rend à part.
// ⚠️ `logement` NU EN FAIT PARTIE (lot 6, revue adverse M5 — mesuré au DOM).
// `compta_ligne_ajouter` déclare son logement sous ce nom-là, SANS suffixe :
// la règle de forme ne l'attrapait pas, et la carte affichait « un logement :
// lc1 » — un slug sur une carte d'argent. Il rejoint la liste plutôt que la
// règle du dessous, et c'est ce que dit déjà le commentaire de
// `clesResoluesDe` : « le logement et les `*_id` ont déjà leur traitement plus
// haut ». Un logement se nomme par `logement.nom` de l'aperçu, pas par le
// `nom` de l'objet visé.
// ⚠️ Et « rien » reste un choix, pas un oubli : `compta_ligne_lire` ne rend pas
// de nom de logement (sa lecture porte sur la sorte et le mois). La ligne
// disparaît donc de cette carte-là, et le logement se constate juste après le
// clic, dans Argent › Compta — ce que dit le `constatable` du levier.
const CLES_LOGEMENT = [
  "logement",
  "logement_id",
  "logementId",
  "target_logement_id",
];

// ══ UN ARGUMENT RÉSOLU N'EST PAS FORCÉMENT UN CODE — LE MOIS SE LIT ════════
//
// ⚠️ INTERACTION DES DEUX LOTS, ET ELLE AURAIT EFFACÉ UNE DONNÉE D'ARGENT.
// La règle du dessous masque tout ce que `args_resolus` déclare, sauf ce qui
// porte aussi une liste fermée (`args_valeurs`). Le lot 6 fait RÉSOUDRE `mois`
// sur les cinq leviers de facturation et de compta — il sort du séjour ou de
// la ligne désignée, il n'est plus composé par le modèle. Il serait donc
// masqué : la carte qui corrige un net hôte ne dirait plus SUR QUEL MOIS.
//
// Or un mois n'est pas un code : il se lit (« septembre 2026 », cf.
// `moisEnClair`), et c'est précisément ce que Dimitri doit vérifier avant de
// cliquer — c'est lui qui décide quelle facture bouge. Il est donc exempté,
// pour la même raison qu'`args_valeurs` : vocabulaire métier, pas rouage.
const CLES_RESOLUES_LISIBLES = ["mois"];

const CLES_RESOLUES = new Map();

/**
 * `{ masquees: Set, objet: string|null }` — ce que le levier fait résoudre, et
 * lequel de ces arguments EST la chose dont l'aperçu rend le nom.
 */
function clesResoluesDe(nomLevier) {
  if (CLES_RESOLUES.has(nomLevier)) return CLES_RESOLUES.get(nomLevier);
  const l = nomLevier ? lireLevier(nomLevier) : null;
  const masquees = new Set();
  let objet = null;
  for (const chemin of Object.keys((l && l.args_resolus) || {})) {
    const racine = chemin.split(".")[0];
    if ((l.args_valeurs || {})[racine]) continue; // vocabulaire métier
    if (CLES_RESOLUES_LISIBLES.includes(racine)) continue; // se lit, cf. ci-dessus
    masquees.add(racine);
    // Le logement et les `*_id` ont déjà leur traitement plus haut : ils ne
    // peuvent pas être l'objet nommé de cette règle-ci.
    if (!objet && !estIdentifiant(racine) && !CLES_LOGEMENT.includes(racine))
      objet = racine;
  }
  const lu = { masquees, objet };
  CLES_RESOLUES.set(nomLevier, lu);
  return lu;
}

// ══ UN ARGUMENT COMPOSÉ SE DIT, IL NE SE DÉPLIE PAS (revue #1501, M1) ══════
//
// Mesuré : la carte d'un achat rendait « un achat : date : 2028-01-18 ·
// quantite : 2 · unite : paquet · prixTotal : 17.24 · csId : cs9 », et un
// dépôt « … logId : lc1 · csId : cs9 » — les deux codes que le lot 4 existe
// pour cacher, sur le seul écran lu avant un « oui » d'argent. `lisible()`
// dépliait l'objet en « clé : valeur » sans rien filtrer.
//
// Désormais : les feuilles IDENTIFIANTS ne s'affichent jamais (l'article est
// nommé à part, par le nom que l'aperçu a lu) ; les feuilles TAPÉES sur la
// carte ont leur champ plus bas ; le reste se dit en français — « le 18
// janvier 2028 · 2 paquets · Metro ».

/** « 2 paquets », « 6 à l'unité », « 3 unités de distribution ». */
const UNITES_DITES = {
  paquet: (n) => `${n} paquet${n > 1 ? "s" : ""}`,
  unite_prelevable: (n) => `${n} unité${n > 1 ? "s" : ""} de distribution`,
  unite_finale: (n) => `${n} à l'unité`,
};
function quantiteDite(q, unite) {
  const n = Number(q);
  if (!Number.isFinite(n)) return String(q);
  const dire = UNITES_DITES[unite];
  return dire ? dire(n) : String(n);
}
const euroDit = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? `${n.toFixed(2).replace(".", ",")} €` : String(v);
};

/**
 * La ligne d'un argument COMPOSÉ (`achat`, `depot`) — ses feuilles dites, sans
 * identifiant, sans ce qui se tape sur la carte.
 *
 * @param {string} cle — `achat`, `depot`
 * @param {object} v — l'objet composé
 * @param {string[]} feuillesSaisies — les chemins déclarés `args_saisis`
 */
// ⚠️ LES SEULES FEUILLES QU'ON SAIT DIRE — celles d'un achat ou d'un dépôt.
// Un objet composé qui en porte d'autres (les blocs d'un message :
// `{recu:"off", caution:"auto"}`) garde son rendu « clé : valeur » d'avant,
// byte-identique : sans les clés, « off · auto » ne veut plus rien dire.
// L'ordre est celui de la lecture : le jour, la quantité, le prix, le reste.
export const FEUILLES_DITES = Object.freeze([
  "date",
  "quantite",
  "unite",
  "prixTotal",
  "enseigne",
  "notes",
  "initiales",
]);

/** L'objet composé se dit-il feuille par feuille (achat, dépôt) ? */
export function seDitSansCles(v) {
  if (!v || typeof v !== "object" || Array.isArray(v)) return false;
  const cles = Object.keys(v);
  return (
    cles.length > 0 &&
    cles.every((k) => estIdentifiant(k) || FEUILLES_DITES.includes(k))
  );
}

export function ligneComposee(
  cle,
  v,
  feuillesSaisies = [],
  nomLogement = null,
) {
  const unite = v.unite;
  const dire = (k, x) => {
    if (k === "quantite") return quantiteDite(x, unite);
    if (k === "date") return `le ${jourEnClair(x)}`;
    if (/^prix/i.test(k) || k === "montant") return euroDit(x);
    return String(x);
  };
  const presentes = Object.keys(v).filter(
    (k) =>
      v[k] !== undefined &&
      v[k] !== null &&
      v[k] !== "" &&
      !estIdentifiant(k) &&
      !feuillesSaisies.includes(`${cle}.${k}`) &&
      k !== "unite", // dite avec la quantité
  );
  // Le jour, la quantité, le prix d'abord ; le reste dans l'ordre de l'objet.
  const ordre = ["date", "quantite", "prixTotal", "montant"];
  const bouts = [
    ...ordre.filter((k) => presentes.includes(k)),
    ...presentes.filter((k) => !ordre.includes(k)),
  ].map((k) => dire(k, v[k]));
  // ⚠️ LE LOGEMENT EN TÊTE, PAR SON NOM (revue #1501 v2, N1). `logId` est un
  // identifiant, donc masqué — et un dépôt qui impute un euro à un logement
  // ne peut pas le taire. Le nom vient de l'APERÇU (`logement.nom`) ; sans
  // nom lu, rien : jamais `lc1` en repli.
  const porteUnLogement = Object.keys(v).some((k) =>
    ["logId", "logement_id", "logementId"].includes(k),
  );
  if (porteUnLogement && nomLogement) bouts.unshift(`à ${nomLogement}`);
  return {
    cle,
    libelle: ARGS_LIBELLES[cle] || cle,
    valeur: bouts.length ? bouts.join(" · ") : "—",
  };
}

// ══ LE SIGNE D'UN AJUSTEMENT SE DIT SUR CE QUI EST TAPÉ ════════════════════
//
// ⚠️ LA GARDE VIVAIT AU MAUVAIS ENDROIT, ET ELLE NE POUVAIT JAMAIS SE DIRE
// (revue adverse #1508, M3). `data_ajustement_ajouter.montant` est une saisie
// INCONDITIONNELLE : le serveur la retire TOUJOURS des arguments avant
// d'appeler ses gardes. Celle qui disait « 50 € en PLUS : c'est un supplément,
// pas une remise » lisait donc un argument qui n'existait plus — une garde
// morte, avec un test qui la croyait vivante parce qu'il lui injectait la
// valeur à la main (motif « fixtures écrites pour passer », 15/09).
//
// Elle vit désormais là où la valeur existe : sous le champ, au rendu, sur ce
// que Dimitri vient de taper. ⚠️ ET C'EST UN AVERTISSEMENT, PAS UN BLOCAGE :
// un supplément est parfaitement légitime — un ménage en plus, une réparation
// refacturée. Ce qui ne l'est pas, c'est de croire qu'on déduit quand on
// ajoute : l'intitulé (« Remise commerciale ») paraît TEL QUEL sur la facture
// du propriétaire.
const LEVIERS_AJUSTEMENT_SIGNE = ["data_ajustement_ajouter"];

export function avertissementDuSigne(levier, chemin, brut) {
  if (!LEVIERS_AJUSTEMENT_SIGNE.includes(levier) || chemin !== "montant")
    return null;
  const n = valeurSaisie(brut, "montant_signe");
  if (n === null || n <= 0) return null;
  return `${euroDit(n)} en PLUS : c'est un supplément, pas une remise. Une remise se tape en moins.`;
}

/** L'objet porte-t-il, à plat ou dans une feuille, l'identifiant d'un article ? */
function porteUnArticle(args) {
  for (const [k, v] of Object.entries(args || {})) {
    if (k === "csId" || k === "cs_id") return true;
    if (v && typeof v === "object" && !Array.isArray(v) && porteUnArticle(v))
      return true;
  }
  return false;
}

// Les seules clés d'identifiant qu'on sait remplacer par un NOM : celui que la
// lecture d'aperçu a rendu. `target_logement_id` EST le logement de cette
// lecture (« copier VERS ») ; `source_logement_id` a son propre nom, que la
// lecture rend à part — sans lui, « Récupérer depuis X » ne nommait plus
// personne (revue #1487).
const LIBELLES_COPIE = {
  source_logement_id: "un logement de départ",
  target_logement_id: "un logement d'arrivée",
};

/**
 * Les lignes du bloc « arguments » — libellé glosé, valeur sans code.
 *
 * @param {object|null} geste
 * @param {{clesTextes?:string[], nomLogement?:string|null}} ctx
 * @returns {{cle:string, libelle:string, valeur:string}[]}
 */
export function lignesDArguments(geste, ctx = {}) {
  const clesTextes = ctx.clesTextes || [];
  const nomLogement = ctx.nomLogement || null;
  const nomSource = ctx.nomSource || null;
  const libelleChamp = ctx.libelleChamp || null;
  const nomArticle = ctx.nomArticle || null;
  // Le nom que l'APERÇU a lu pour l'objet visé — la seule source. Sans lui,
  // une valeur technique ne se remplace par rien : la ligne est absente.
  const nomLu = ctx.nomLu || null;
  const resolues = clesResoluesDe(geste && geste.levier);
  const feuillesSaisies = ctx.feuillesSaisies || [];
  const args = (geste && geste.args) || {};
  const lignes = [];
  // ⚠️ L'ARTICLE D'ABORD, PAR SON NOM — lu par l'aperçu (`article.nom`),
  // jamais `cs9`. Sans nom lu, pas de ligne : un code affiché se lit comme
  // une donnée et s'oublie.
  if (nomArticle && porteUnArticle(args))
    lignes.push({
      cle: "article",
      libelle: ARGS_LIBELLES.article || "un article",
      valeur: String(nomArticle),
    });
  for (const [cle, v] of Object.entries(args)) {
    // Un texte a son champ plus bas — il ne se lit pas deux fois.
    if (clesTextes.includes(cle)) continue;
    // Un achat, un dépôt se DISENT feuille par feuille — jamais dépliés en
    // « clé : valeur » (leurs identifiants ne s'affichent pas). Les autres
    // objets composés gardent leur rendu d'avant, plus bas (`lisible`).
    if (seDitSansCles(v)) {
      lignes.push(ligneComposee(cle, v, feuillesSaisies, nomLogement));
      continue;
    }
    if (cle === "source_logement_id") {
      if (!nomSource) continue;
      lignes.push({
        cle,
        libelle: LIBELLES_COPIE.source_logement_id,
        valeur: String(nomSource),
      });
      continue;
    }
    if (CLES_LOGEMENT.includes(cle)) {
      if (!nomLogement) continue;
      lignes.push({
        cle,
        libelle: LIBELLES_COPIE[cle] || ARGS_LIBELLES.logement,
        valeur: String(nomLogement),
      });
      continue;
    }
    // ⚠️ LE MÉNAGE, L'INCIDENT, LA LIGNE : leur identifiant ne dit rien, et
    // ce qu'il désigne est porté par l'EN-TÊTE DE L'APERÇU, qui le lit au
    // serveur (« Cocon Naturel — le ménage de samedi »). C'est la seule
    // source : le repère prononcé (`vise`) ne sort JAMAIS vers l'écran —
    // `lib/reperes-geste.js` le remet à `undefined` avant de répondre.
    if (estIdentifiant(cle)) continue;
    // ⚠️ LA VALEUR DE `champ` EST, ELLE AUSSI, UN NOM DE COLONNE
    // (`commission_taux`). Le seul qui sache le dire en français est
    // l'aperçu, qui le LIT au serveur (`libelle`) — on n'invente pas une
    // seconde table ici. Sans lecture (aperçu en cours, ou levier sans
    // aperçu), la valeur brute reste : mieux qu'une ligne muette.
    if ((cle === "champ" || cle === "field") && libelleChamp) {
      lignes.push({
        cle,
        libelle: ARGS_LIBELLES[cle] || cle,
        valeur: libelleChamp,
      });
      continue;
    }
    // ⚠️ UN BOOLÉEN SE LIT PAR CE QU'IL FAIT (prod du 22/09 : « une activation
    // ou une désactivation · true »). La phrase dépend du LEVIER — `active`
    // lève un verrou ici, allume un multiplicateur là — et vit dans le
    // registre (`BOOLEENS_LIBELLES`), jamais ici.
    if (typeof v === "boolean") {
      const mots = motsDuBooleen(geste && geste.levier, cle, v);
      lignes.push({ cle, libelle: mots.libelle, valeur: mots.valeur });
      continue;
    }
    // ⚠️ UNE VALEUR QUI EST UN CODE SE DIT PAR SON NOM, OU NE SE DIT PAS
    // (prod du 23/09 : « un gabarit · caution_reminder_j2 »). Le nom vient de
    // l'aperçu, qui l'a LU au serveur — jamais d'une table d'ici, jamais du
    // repère prononcé. Et sans nom lu : rien. Une ligne absente se remarque
    // et se corrige ; un code affiché se lit comme une donnée et s'oublie —
    // c'est le raisonnement déjà tenu pour `lc1`.
    // ⚠️ UN ARGUMENT QUE LE SERVEUR RÉSOUT EST UN CODE, QUELLE QUE SOIT SA
    // FORME (prod du 23/09 : « un gabarit · caution_reminder_j2 »). Il se dit
    // par le nom que l'APERÇU a lu — jamais d'une table d'ici, jamais du
    // repère prononcé, que `lib/reperes-geste.js` retire — et sans nom lu,
    // rien : le raisonnement déjà tenu pour `lc1`. Une ligne absente se
    // remarque et se corrige ; un code affiché se lit comme une donnée et
    // s'oublie.
    if (resolues.masquees.has(cle)) {
      // ⚠️ LE NOM LU NE VAUT QUE POUR L'OBJET DU GESTE (M5). Les autres
      // arguments résolus sont des rouages du même objet — un `place_id`, un
      // lien Tisséo — et leur coller le nom lu affiche « un lien Tisséo · Le
      // Bibent » : un nom juste sur une ligne qui désigne autre chose.
      if (!nomLu || cle !== resolues.objet) continue;
      lignes.push({
        cle,
        libelle: ARGS_LIBELLES[cle] || cle,
        valeur: String(nomLu),
      });
      continue;
    }
    lignes.push({
      cle,
      libelle: ARGS_LIBELLES[cle] || cle,
      valeur: valeurDite(cle, v),
    });
  }
  return lignes;
}

// ══ CE QUE DIMITRI CHOISIT SUR LA CARTE (2026-09-13) ═══════════════════════
//
// Certains arguments ne se composent pas : l'agent d'un ménage est un compte,
// donc un e-mail — que ni le moteur d'événements ni le modèle n'ont le droit
// de lire ou d'inventer. Le levier les déclare en `args_choisis`, et c'est la
// carte qui les demande, dans une liste que le SERVEUR rend à l'instant de
// l'affichage (une action de lecture, avec le jeton de Dimitri).
//
// ⚠️ LE BOUTON RESTE GRIS TANT QUE RIEN N'EST CHOISI, et une liste qui n'a pas
// pu être lue le dit — jamais un menu vide qui ressemble à « personne ».
// ══ LE GUICHET VIENT DU REGISTRE, ET DE LA LISTE BLANCHE (revue adverse
// #1449, MAJEUR 1, 2026-09-16) ═════════════════════════════════════════════
//
// ⚠️ DÉFAUT STRUCTUREL, ET IL PARTAIT AVANT LE CLIC. Les deux lectures de
// cette carte postaient sur `geste.endpoint` — c'est-à-dire sur ce que la
// PROPOSITION portait (`operateur_propositions`), pas sur ce que le registre
// déclare, alors que deux lignes plus haut la même fonction relisait déjà
// l'aperçu depuis le registre. Et elles partent d'un `useEffect` au RENDU :
// donc avant tout clic, et avant la garde `ENDPOINTS_AUTORISES` d'
// `executerGeste`, qui ne s'exerce qu'à l'exécution. Une proposition dont
// `endpoint` vaudrait `/api/stripe` (ligne forgée, migration bâclée, rejeu
// d'une proposition d'un lot futur) aurait fait partir un POST authentifié
// admin à l'AFFICHAGE de la carte. Plus banalement, un `geste.endpoint`
// divergent du registre faisait lire un guichet et écrire sur un autre, sans
// que rien ne compare les deux.
//
// D'où : le guichet est celui du REGISTRE, et il doit être dans la liste
// blanche — sinon rien ne part. FAIL-CLOSED, et avant le `fetch`.
function guichetDuLevier(levierLu) {
  const e = levierLu && levierLu.endpoint;
  return ENDPOINTS_AUTORISES.includes(e) ? e : null;
}

/** Le message quand aucun guichet n'est ouvert — il nomme ce qui a été refusé. */
function _refusDeGuichet(levierLu) {
  const e = (levierLu && levierLu.endpoint) || "aucun";
  return `guichet non autorisé pour ce levier (${e}) — rien n'a été lu`;
}

async function lireListeChoix(geste, cle, desc, endpoint) {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error("Session expirée");
  const corps = { action: desc.lecture };
  for (const dep of desc.depend_de || []) corps[dep] = (geste.args || {})[dep];
  const res = await fetch(endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${session.access_token}`,
    },
    body: JSON.stringify(corps),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok)
    throw new Error(
      json.message || json.error || `Lecture refusée (${res.status})`,
    );
  const options = Array.isArray(json.choix) ? json.choix : [];
  return {
    options: options.filter(
      (o) => o && typeof o.valeur === "string" && typeof o.libelle === "string",
    ),
    // Le serveur dit si quelqu'un porte déjà la valeur — pour que l'écrasement
    // se lise AVANT le clic (décision Dimitri, 2026-09-13).
    assigne: json.assigne === true,
  };
}

// ══ CE QUE LA CARTE MONTRE AVANT LE CLIC — L'APERÇU (2026-09-14) ═══════════
//
// « Modifie ce champ de cette fiche » : sans l'ANCIENNE valeur, Dimitri ne
// peut pas juger. Le modèle ne connaît pas la fiche (et un code de porte n'a
// rien à faire dans un prompt) : c'est la carte qui lit, avec le jeton de
// Dimitri, l'action de lecture que le levier nomme (`apercu.lecture`), et qui
// affiche « logement · le champ en français · actuellement X → Y · où le
// constater ». Elle garde le JETON DE VERSION rendu par la lecture : le geste
// part avec, et le serveur refuse si la base a bougé entre-temps.
//
// ⚠️ LE BOUTON RESTE GRIS TANT QUE L'APERÇU N'EST PAS LU — une lecture qui
// échoue le dit, elle ne laisse pas cliquer sur une promesse « X → Y » dont on
// ne connaît pas le X. Et si la lecture rend des valeurs fermées, la valeur
// composée est vérifiée contre elles ICI, avant le clic : un geste qui
// échouerait sous le doigt est pire qu'un geste non proposé.
async function lireApercu(geste, desc, endpoint) {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error("Session expirée");
  const corps = { action: desc.lecture };
  for (const dep of desc.depend_de || []) corps[dep] = (geste.args || {})[dep];
  // Les bornes de la période, dites par le LEVIER (inclusive pour un prix,
  // exclusive pour un blocage) : la lecture les exige, la carte ne les devine
  // pas. Un levier sans période n'en déclare pas, et rien ne part.
  if (typeof desc.bornes === "string") corps.bornes = desc.bornes;
  const res = await fetch(endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${session.access_token}`,
    },
    body: JSON.stringify(corps),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok)
    throw new Error(
      json.message || json.error || `Lecture refusée (${res.status})`,
    );
  if (typeof json.cfg_version !== "string" || !json.cfg_version)
    throw new Error("la lecture n'a pas rendu de jeton de version");
  return json;
}

// ══ UNE LECTURE POUR PLUSIEURS LEVIERS (2026-09-15) ════════════════════════
//
// `pricing_config_lire` rend le prix de base, le plancher ET le séjour minimum
// d'un logement : trois leviers s'en servent, chacun n'en montrant qu'un. Le
// levier déclare LEQUEL (`apercu.champ`) et QUEL ARGUMENT porte la nouvelle
// valeur (`apercu.valeur_arg` — « deux nuits » arrive sous `min_stay`, pas
// sous `nouvelle`). Sans `champ`, c'est la réponse entière qui fait bloc :
// le comportement des quatre lectures d'avant, inchangé.
function blocApercu(apercuData, desc) {
  if (!apercuData) return null;
  if (!desc || !desc.champ) return apercuData;
  const c = apercuData.champs && apercuData.champs[desc.champ];
  return c && typeof c === "object" ? c : null;
}

/**
 * La nouvelle valeur, sous le nom d'argument que le levier déclare.
 *
 * ⚠️ CE QUE DIMITRI A TAPÉ PASSE DEVANT (lot 6). Quand ce chemin est une
 * saisie de la carte (`args_saisis`, sous sa condition), le serveur l'a
 * RETIRÉ des arguments : l'« après » de l'aperçu (« 20 % → 18 ») ne peut
 * venir que du champ tapé — validé selon sa nature, jamais brut. Tant que rien
 * n'est tapé, il n'y a pas d'« après », et le bouton est gris de toute façon.
 */
function nouvelleValeur(geste, desc, saisies) {
  const cle = (desc && desc.valeur_arg) || "nouvelle";
  const levier = geste && lireLevier(geste.levier);
  const decl = levier && levier.args_saisis && levier.args_saisis[cle];
  if (decl && saisieSApplique(decl, (geste && geste.args) || {})) {
    const v = valeurSaisie((saisies || {})[cle], decl.nature);
    return v === null ? undefined : v;
  }
  return ((geste && geste.args) || {})[cle];
}

/** La valeur composée, hors des valeurs fermées rendues par la lecture ? */
function valeurHorsListe(bloc, geste, desc, saisies) {
  if (!bloc || !Array.isArray(bloc.valeurs)) return null;
  const v = nouvelleValeur(geste, desc, saisies);
  if (v === null || v === undefined) return null;
  return bloc.valeurs.includes(String(v)) ? null : bloc.valeurs;
}

// ══ CE QUE DIMITRI RELIT ET CORRIGE SUR LA CARTE (2026-09-13) ══════════════
//
// Dimitri : « tu as raison de refuser qu'un texte tout fait parte d'un clic.
// Avec le champ, je lis, je corrige si besoin, j'envoie. »
//
// Un argument déclaré dans `args_textes` par son levier (le corps d'une réponse
// au voyageur) arrive COMPOSÉ, et il est destiné à un tiers : il s'affiche
// dans un champ, pré-rempli, que Dimitri peut réécrire avant d'envoyer. C'est
// le texte du champ qui part — `executerGeste` le substitue à l'original.
//
// ⚠️ SEULEMENT LE TEXTE, JAMAIS LES CIBLES. Un identifiant, une date, un
// montant restent en toutes lettres et NON éditables : on ouvre ce qui se
// relit, pas ce qui désigne. Et un champ vidé bloque le geste — un envoi à
// corps vide serait pire qu'un refus.
function clesTextesDe(geste) {
  const levier = geste && lireLevier(geste.levier);
  const declares = (levier && levier.args_textes) || [];
  const args = (geste && geste.args) || {};
  return declares.filter((c) => Object.prototype.hasOwnProperty.call(args, c));
}

function textesInitiaux(geste) {
  const out = {};
  for (const c of clesTextesDe(geste)) {
    const v = (geste.args || {})[c];
    out[c] = typeof v === "string" ? v : "";
  }
  return out;
}

// ⚠️ `forwardRef` DEPUIS LE 2026-09-15. La carte expose UNE méthode, `lancer`,
// et rien d'autre : c'est par elle que le « oui » tapé dans la conversation
// passe — donc par le MÊME chemin que le clic, avec les mêmes gardes. Exposer
// l'exécution plutôt que dupliquer le fetch est ce qui garantit qu'un « oui »
// ne peut pas sauter un motif, un choix ou un aperçu.
function CarteGesteDemande({ geste, onFait }, ref) {
  const [etat, setEtat] = useState("propose"); // propose | envoi | fait | echec
  const [motif, setMotif] = useState("");
  // ⚠️ LA CONFIRMATION DE DIMITRI, POUR LES GESTES QUI L'EXIGENT (2026-09-17).
  // Elle n'est pas un argument — le modèle l'écrirait. Elle se coche ici, sur
  // la carte, et n'existe nulle part ailleurs : `geste-executer` la transforme
  // en `confirm: true` et refuse de partir sans elle.
  const [confirme, setConfirme] = useState(false);
  // Les textes tels que Dimitri les a corrigés — pré-remplis avec la proposition.
  const [textes, setTextes] = useState(() => textesInitiaux(geste));
  // ⚠️ CE QUE DIMITRI TAPE, ET QUE LE MODÈLE NE COMPOSE JAMAIS (revue #1501,
  // M4) : le prix payé d'un achat. Vide au départ — jamais pré-rempli par la
  // proposition, c'est tout le sens du champ.
  const [saisies, setSaisies] = useState({});
  const [echec, setEchec] = useState(null);
  // ⚠️ CE QUE LE GESTE RAPPORTE (2026-09-14). `executerGeste` rendait
  // `resultat` et PERSONNE ne l'affichait : pour un geste qui ÉCRIT, le
  // `constatable` suffit (l'effet est en base) ; pour un geste qui LIT, le
  // résultat EST l'effet, et le jeter rendait la carte menteuse — elle
  // promettait un rafraîchissement qui n'avait jamais lieu.
  //
  // ⚠️ ON N'AFFICHE PAS `resultat` BRUT, mais la SEULE clé `resume` : une
  // chaîne composée par le SERVEUR, qui seul sait ce qu'il a le droit de dire.
  // Un rendu générique du JSON finirait par montrer une adresse ou un e-mail.
  const [resume, setResume] = useState(null);
  // ⚠️ Anti-double-clic par RÉFÉRENCE, pas par état : deux clics rapides
  // partent avant que React n'ait re-rendu, et le geste se ferait deux fois.
  const enVol = useRef(false);
  // La fonction d'exécution du rendu courant, pour la prise `lancer` (voir plus
  // bas). Une référence, parce qu'elle est définie APRÈS le retour anticipé.
  const lancerRef = useRef(null);
  // Idem pour la composition de ce qui se dit : elle lit des valeurs dérivées
  // PLUS BAS dans le rendu (l'aperçu, le blocage), donc elle se pose après.
  const phraseRef = useRef(() => ({ etat: "absente" }));
  // Les arguments choisis sur la carte : la liste par clé, et le choix fait.
  const [listes, setListes] = useState({}); // cle → {etat, options, assigne, erreur}
  const [choix, setChoix] = useState({}); // cle → valeur
  const choisis = (geste && geste.args_choisis) || {};
  const clesChoisies = Object.keys(choisis);
  const levier = geste && geste.levier;
  // L'aperçu vient du REGISTRE (relu ici, comme `args_textes`), jamais de la
  // carte servie : un levier retiré de l'aperçu entre deux réponses ne doit
  // pas continuer à lire.
  const levierLu = geste && lireLevier(geste.levier);
  const apercuDecl =
    levierLu && levierLu.apercu && typeof levierLu.apercu === "object"
      ? levierLu.apercu
      : null;
  const [apercu, setApercu] = useState({ etat: "aucun", data: null });

  useEffect(() => {
    if (!geste || !apercuDecl) {
      setApercu({ etat: "aucun", data: null });
      return undefined;
    }
    // ⚠️ AVANT TOUT APPEL : si le serveur a déjà lu cet aperçu pour composer
    // sa phrase, on reprend SON résultat et SON jeton. Relire donnerait deux
    // jetons pour une même décision, et effacerait en silence un changement
    // survenu entre ce que Dimitri a LU et son clic.
    const prelu = apercuPrelu(geste, apercuDecl);
    if (prelu) {
      setApercu({ etat: "pret", data: prelu });
      return undefined;
    }
    // ⚠️ AVANT LE `fetch`, PAS APRÈS : un guichet hors liste blanche ne
    // déclenche aucun appel — l'aperçu échoue en le disant.
    const guichet = guichetDuLevier(levierLu);
    if (!guichet) {
      setApercu({
        etat: "echec",
        data: null,
        erreur: _refusDeGuichet(levierLu),
      });
      return undefined;
    }
    const vie = { vivant: true };
    setApercu({ etat: "chargement", data: null });
    lireApercu(geste, apercuDecl, guichet)
      .then((data) => {
        if (vie.vivant) setApercu({ etat: "pret", data });
      })
      .catch((e) => {
        if (!vie.vivant) return;
        console.error("[geste] aperçu", e?.message || e);
        setApercu({
          etat: "echec",
          data: null,
          erreur: e?.message || String(e),
        });
      });
    return () => {
      vie.vivant = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [levier, JSON.stringify((geste && geste.args) || {})]);

  useEffect(() => {
    if (!geste || !clesChoisies.length) return undefined;
    const guichet = guichetDuLevier(levierLu);
    if (!guichet) {
      setListes((l) => {
        const suite = { ...l };
        for (const cle of clesChoisies)
          suite[cle] = {
            etat: "echec",
            options: [],
            erreur: _refusDeGuichet(levierLu),
          };
        return suite;
      });
      return undefined;
    }
    const vie = { vivant: true };
    clesChoisies.forEach((cle) => {
      setListes((l) => ({ ...l, [cle]: { etat: "chargement", options: [] } }));
      lireListeChoix(geste, cle, choisis[cle], guichet)
        .then((r) => {
          if (!vie.vivant) return;
          setListes((l) => ({ ...l, [cle]: { etat: "pret", ...r } }));
        })
        .catch((e) => {
          if (!vie.vivant) return;
          console.error("[geste] liste de choix", cle, e?.message || e);
          setListes((l) => ({
            ...l,
            [cle]: {
              etat: "echec",
              options: [],
              erreur: e?.message || String(e),
            },
          }));
        });
    });
    return () => {
      vie.vivant = false;
    };
    // La liste dépend du levier et de ses arguments, pas de l'objet geste
    // (recréé à chaque réponse).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [levier, JSON.stringify((geste && geste.args) || {})]);

  // Un autre geste sur la même carte = ses textes à lui, pas ceux d'avant
  // (le `useState(init)` ne se re-synchronise pas seul).
  useEffect(() => {
    setTextes(textesInitiaux(geste));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [levier, JSON.stringify((geste && geste.args) || {})]);

  // ══ LA SEULE PRISE OFFERTE À L'EXTÉRIEUR (2026-09-15) ═════════════════════
  // `BlocOperateur` l'appelle quand Dimitri écrit « oui » : le geste part alors
  // par le MÊME chemin que le clic, avec les mêmes gardes. Le crochet est
  // AVANT le retour anticipé — un crochet conditionnel casserait l'ordre des
  // crochets au premier rendu sans geste.
  //
  // ⚠️ ET `phrase()` DEPUIS LE 2026-09-22 (B2). La carte NE PARLE PAS : elle
  // rend ce qu'il y aurait à dire. Un seul appelant de `dire()` existe dans
  // tout `src/` — le mode conversation — et il doit le rester : lire depuis
  // deux endroits ferait parler deux fois.
  //
  // ⚠️ `etat: 'attente'` TANT QUE L'APERÇU N'EST PAS LU. Le guichet du ménage
  // de CLAUDE.md, appliqué ici : lire la carte au montage l'annoncerait « de
  // rien à quarante euros ». On ne lit jamais une valeur qu'on n'a pas encore.
  useImperativeHandle(ref, () => ({
    lancer: async () =>
      lancerRef.current
        ? lancerRef.current()
        : { lance: false, raison: "La carte n'est pas prête." },
    phrase: (opts) => phraseRef.current(opts || {}),
  }));

  // ⚠️ PLUS DE RETOUR ANTICIPÉ ICI (revue adverse #1432, mineur 6). Il coupait
  // le composant en deux : tout ce qui suit — dont la fonction d'exécution —
  // vivait après le dernier crochet, ce qui obligeait à POSER LA PRISE PENDANT
  // LE RENDU (`lancerRef.current = executer`), une écriture impure que React
  // déconseille. Sans retour anticipé, l'effet ci-dessous est inconditionnel,
  // et le rendu final se contente de ne rien dessiner s'il n'y a pas de geste.
  const g = geste || {};
  const marquages = g.marquages || [];
  // ⚠️ DÉJÀ GLOSÉ PAR LE SERVEUR (`lib/operateur-geste.js`) : des textes, pas
  // des clés. Un geste servi par une version antérieure n'en porte pas — le
  // bloc ne s'affiche alors pas, plutôt que d'afficher des cases vides.
  const effets = {
    ou: Array.isArray(g.ou_ca_part)
      ? g.ou_ca_part.filter((o) => o && o.texte)
      : [],
    cout: Array.isArray(g.cout) ? g.cout.filter((c) => c && c.texte) : [],
  };
  const clesTextes = clesTextesDe(geste);
  // ⚠️ CE QUE LE SERVEUR EXIGE ET QUE LE GESTE N'A PAS (revue adverse #1440).
  // « Mets le séjour minimum à deux nuits du 20 au 31 janvier 2028 » compose
  // `pricing_set_manual` sans prix : le serveur répond 400. Le serveur le dit
  // déjà (`manque_indispensable`) ; la carte le RELIT au registre, comme
  // `executerGeste`, pour qu'une carte servie avant cette règle grise aussi.
  const manqueIndisp =
    (g.manque_indispensable &&
    typeof g.manque_indispensable === "object" &&
    typeof g.manque_indispensable.phrase === "string"
      ? g.manque_indispensable
      : null) ||
    (g.levier ? manqueIndispensable(g.levier, g.args || {}) : null);
  const manqueMotif = g.motif_requis && !motif.trim();
  // Les valeurs à taper, lues au REGISTRE (comme l'aperçu et les textes).
  // ⚠️ SOUS LEUR CONDITION (lot 6) : `nouvelle` ne se tape que quand le champ
  // corrigé est un montant ; pour un libellé, pas de champ à taper — et c'est
  // `manqueIndispensable` (même déclaration) qui grise si elle manque.
  const saisiesDecl =
    levierLu && levierLu.args_saisis && typeof levierLu.args_saisis === "object"
      ? Object.entries(levierLu.args_saisis).filter(([, decl]) =>
          saisieSApplique(decl, g.args || {}),
        )
      : [];
  const manqueSaisie = saisiesDecl.some(
    ([chemin, decl]) => valeurSaisie(saisies[chemin], decl.nature) === null,
  );
  const manqueConfirmation = g.confirmation_au_clic === true && !confirme;
  const manqueChoix = clesChoisies.some((c) => !choix[c]);
  const texteVide = clesTextes.some((c) => !String(textes[c] || "").trim());
  const texteTropLong = clesTextes.some(
    (c) => String(textes[c] || "").length > MAX_CARS_TEXTE,
  );
  const manqueTexte = texteVide || texteTropLong;
  // ⚠️ L'OUVERTURE SE LIT AVANT LE CLIC. Un geste fermé (ou qui ne dit pas
  // qu'il est ouvert) a un bouton gris ET une phrase — `executerGeste`
  // refuserait de toute façon, mais un bouton qui promet et échoue est pire
  // qu'un bouton gris. Même juge (`_refusDeVague`), jamais une règle à côté —
  // et ce juge ne fait plus que LIRE le verdict du serveur, qui a tranché
  // levier par levier.
  const levierRegistre = lireLevier(g.levier);
  const vagueFermee = levierRegistre
    ? _refusDeVague(
        { levier: g.levier, geste_ouvert: g.geste_ouvert },
        levierRegistre,
      )
    : // ⚠️ LEVIER DISPARU DU REGISTRE : le bouton redevenait ACTIF, sans un
      // mot (revue du 2026-09-28). C'est le cas d'un geste resté dans le fil
      // pendant un déploiement qui retire l'action. Il ne part de toute façon
      // pas (l'exécuteur refuse à l'étape « levier ») — mais il le promettait.
      // Même famille de phrase que le refus serveur pour un levier inconnu.
      {
        ok: false,
        etape: "levier",
        message:
          "Cette action n'existe plus dans l'application — rien ne peut partir d'ici. Rien n'a été fait.",
      };
  // L'aperçu déclaré doit être LU (jeton en main) et la valeur composée doit
  // entrer dans les valeurs fermées qu'il rend — sinon le bouton reste gris.
  const manqueApercu = !!apercuDecl && apercu.etat !== "pret";
  // Le bloc du champ visé (ou la réponse entière pour les lectures à un champ).
  // `null` alors que la lecture a réussi = le champ déclaré n'est pas dans ce
  // qu'elle rend : on ne devine pas, le bouton reste gris.
  const blocLu =
    apercu.etat === "pret" ? blocApercu(apercu.data, apercuDecl) : null;
  const champAbsentDeLaLecture = apercu.etat === "pret" && !blocLu;
  // Le NOM du logement, tel que la carte le connaît : l'aperçu, qui l'a LU au
  // serveur avec le jeton de Dimitri. Et rien d'autre — le navigateur n'a pas
  // la liste des logements, et le repère prononcé n'arrive pas jusqu'ici
  // (`lib/reperes-geste.js` le retire). Jamais `lc1` : sans nom, pas de ligne.
  const nomLogementDit =
    (apercu.data && apercu.data.logement && apercu.data.logement.nom) || null;
  const lignesArgs = lignesDArguments(geste, {
    clesTextes,
    nomLogement: nomLogementDit,
    // L'article d'un geste de stock, par son nom — l'aperçu l'a lu.
    nomArticle:
      (apercu.data && apercu.data.article && apercu.data.article.nom) || null,
    // Les feuilles qui se tapent sur la carte ont leur champ plus bas.
    feuillesSaisies: saisiesDecl.map(([chemin]) => chemin),
    // Le logement de DÉPART d'une copie : la lecture le nomme à part.
    nomSource:
      (apercu.data && apercu.data.source && apercu.data.source.nom) || null,
    libelleChamp: (blocLu && blocLu.libelle) || null,
    // Le nom lu pour l'objet visé, quand l'aperçu en rend un : c'est lui qui
    // remplace un code (`caution_reminder_j2`). Sans lui, la ligne disparaît.
    nomLu: (apercu.data && apercu.data.nom) || null,
  });
  const horsListe =
    apercu.etat === "pret"
      ? valeurHorsListe(blocLu, g, apercuDecl, saisies)
      : null;
  const gelLu =
    apercu.etat === "pret" && apercu.data && apercu.data.gel
      ? apercu.data.gel
      : null;
  // ⚠️ CE QUE LA LECTURE SAIT DÉJÀ ET QUE L'ÉCRITURE REFUSERA. Le prix de base
  // et le séjour minimum sont refusés hors tarification manuelle ; la lecture
  // suit le MÊME prédicat et rend la phrase. Un bouton qui promet et échoue
  // sous le doigt est pire qu'un bouton gris.
  const refusLu =
    blocLu && typeof blocLu.refus === "string" && blocLu.refus.trim()
      ? blocLu.refus.trim()
      : null;
  // Y a-t-il un « après » à annoncer ? Un levier qui ne déclare pas
  // `valeur_arg` n'en a pas ; un argument facultatif absent non plus.
  const valeurSuivante = apercuDecl
    ? nouvelleValeur(geste, apercuDecl, saisies)
    : undefined;
  const aUneSuite = !apercuDecl
    ? false
    : // Les lectures à UN champ (`logement_champ_lire` et les siennes) n'ont
      // jamais déclaré `valeur_arg` : leur « après » est `nouvelle`, et il est
      // toujours là — sauf quand `nouvelle` se TAPE (lot 6) : alors il n'y a
      // d'après que tapé, sinon la flèche pointerait sur « — ».
      (!apercuDecl.champ &&
        (valeurSuivante !== undefined ||
          !saisiesDecl.some(([c]) => c === "nouvelle"))) ||
      (!!apercuDecl.valeur_arg &&
        valeurSuivante !== undefined &&
        valeurSuivante !== null &&
        valeurSuivante !== "");
  const bloque =
    !!manqueIndisp ||
    manqueMotif ||
    manqueSaisie ||
    manqueConfirmation ||
    manqueChoix ||
    manqueTexte ||
    manqueApercu ||
    champAbsentDeLaLecture ||
    !!refusLu ||
    !!horsListe ||
    (gelLu && gelLu.gele === true) ||
    !!vagueFermee;

  // ══ POURQUOI LE BOUTON EST GRIS — EN UNE PHRASE ═══════════════════════════
  //
  // ⚠️ AJOUTÉ LE 2026-09-15, AVEC « OUI » QUI VAUT LE CLIC. Un bouton gris se
  // voit : Dimitri lit la carte, comprend qu'il manque le motif, l'écrit.
  // Un « oui » tapé dans la conversation, lui, ne voit RIEN — et un « oui » qui
  // ne produit aucun effet et aucune phrase est indiscernable d'une panne.
  // Chaque blocage doit donc savoir se dire. Ce sont EXACTEMENT les conditions
  // déjà écrites au-dessus (elles ne sont pas réécrites, seulement nommées),
  // plus celles de `geste-executer.js`, qui refuserait de toute façon.
  function raisonDuBlocage() {
    if (etat === "fait") return "C'est déjà fait — je ne le refais pas.";
    if (etat === "envoi" || enVol.current) return "C'est en cours.";
    if (vagueFermee) return vagueFermee.message;
    if (manqueIndisp)
      return `${manqueIndisp.phrase} — ce geste ne partira pas. Rien n'a été fait.`;
    if (manqueMotif)
      return "Il me faut le motif avant de lancer — écrivez-le sur la carte.";
    if (manqueSaisie) {
      const [, decl] =
        saisiesDecl.find(
          ([chemin, d]) => valeurSaisie(saisies[chemin], d.nature) === null,
        ) || [];
      return `Il me faut ${(decl && decl.libelle) || "la valeur demandée"} avant de lancer — tapez-le sur la carte.`;
    }
    if (manqueConfirmation)
      return "Il me faut votre confirmation avant de lancer — cochez-la sur la carte.";
    if (manqueChoix)
      return "Il me faut votre choix avant de lancer — sélectionnez-le sur la carte.";
    if (manqueApercu)
      return "Je n'ai pas encore lu la valeur actuelle — ce geste ne part pas sans elle. Rien n'a été fait.";
    if (champAbsentDeLaLecture)
      return "La valeur actuelle n'est pas dans ce que j'ai lu — ce geste ne partira pas. Rien n'a été fait.";
    if (refusLu) return `${refusLu}. Ce geste ne partira pas.`;
    if (horsListe)
      return `Valeur hors liste — attendu : ${horsListe.join(" | ")}. Ce geste ne partira pas.`;
    if (gelLu && gelLu.gele === true)
      return `${gelLu.label} est déjà facturé pour ce logement — ce champ ne se modifie plus pour ce mois. Ce geste ne partira pas.`;
    if (texteTropLong)
      return "Le texte dépasse la longueur permise — corrigez-le sur la carte.";
    if (texteVide)
      return "Le texte est vide — relisez-le sur la carte avant de lancer.";
    return "Ce geste ne peut pas partir en l'état — regardez la carte.";
  }

  /**
   * Exécute, ou dit pourquoi il ne le fait pas.
   *
   * ⚠️ UN SEUL CHEMIN POUR LE CLIC ET POUR LE « OUI » (2026-09-15). Le bouton
   * l'appelle, et `BlocOperateur` l'appelle par la référence quand Dimitri
   * écrit « oui ». Deux chemins parallèles divergeraient au premier correctif
   * appliqué d'un seul côté — et ici, diverger voudrait dire qu'un « oui »
   * saute une garde que le clic respecte.
   *
   * @returns {Promise<{lance:boolean, ok?:boolean, raison?:string}>}
   */
  async function executer() {
    // ⚠️ `etat === "fait"` EST UNE GARDE, pas une redondance : le bouton
    // disparaît après le geste, mais l'appel par référence, lui, resterait
    // possible. Un geste fait ne se refait pas depuis cette carte.
    if (etat === "fait" || enVol.current || bloque)
      return { lance: false, raison: raisonDuBlocage() };
    enVol.current = true;
    setEtat("envoi");
    setEchec(null);
    // ⚠️ Pas de `postAction` : ce geste n'a pas de ligne en base. Confirmer une
    // ligne inexistante annoncerait « le geste est parti mais sa trace a
    // manqué » — faux, et inquiétant.
    const r = await executerGeste({
      proposition: {
        levier: geste.levier,
        args: geste.args,
        motif,
        // La confirmation voyage à part du modèle, comme le motif tapé.
        confirme,
        choix,
        // Le texte corrigé — c'est lui qui part, et lui qui est tracé.
        textes: clesTextes.length ? textes : undefined,
        // Ce que Dimitri a tapé (le prix payé) — posé au clic, jamais composé.
        saisies: saisiesDecl.length ? saisies : undefined,
        // Le verdict d'ouverture, tel que le serveur l'a marqué.
        geste_ouvert: geste.geste_ouvert,
        // Le jeton de version lu avec l'aperçu — l'écriture l'exige.
        apercu:
          apercu.etat === "pret" && apercu.data
            ? { jeton: apercu.data.cfg_version }
            : undefined,
      },
    });
    enVol.current = false;
    if (r.ok) {
      const brut = r.resultat && r.resultat.resume;
      setResume(
        typeof brut === "string" && brut.trim()
          ? brut.trim().slice(0, MAX_CARS_RESUME)
          : null,
      );
      setEtat("fait");
      if (onFait) onFait(geste, r);
      return { lance: true, ok: true };
    }
    setEtat("echec");
    setEchec(r);
    // ⚠️ L'ÉCHEC SE DIT AUSSI (2026-09-15). `onFait` n'était appelé qu'en cas
    // de succès : le fil ne gardait donc QUE les gestes réussis, et
    // l'opérateur, au tour suivant, ne pouvait pas savoir qu'un geste avait
    // été refusé — au mieux il se taisait, au pire il le reproposait. Les
    // trois natures d'échec voyagent telles quelles (voir `geste-fait.js`) :
    // « refusé » et « on ne sait pas si c'est parti » ne se disent pas pareil.
    if (onFait) onFait(geste, r);
    return { lance: true, ok: false };
  }

  // ⚠️ LA PRISE EST POSÉE PLUS HAUT (avec les autres crochets) ; ici on ne fait
  // que lui donner la fonction DU RENDU COURANT, APRÈS le rendu et à chaque
  // rendu (aucune liste de dépendances). Une fermeture périmée lancerait le
  // geste avec le motif d'AVANT — le piège nommé dans CLAUDE.md, ici sur un
  // chemin qui écrit.
  useEffect(() => {
    lancerRef.current = executer;
    // ⚠️ MÊME RAISON, MÊME EFFET : une fermeture périmée lirait la valeur
    // d'AVANT — et une carte lue au passé est exactement le risque que ce lot
    // ferme (« valider à la voix une carte que je n'ai pas entendue »).
    phraseRef.current = ({ prose = "", vanneOuverte = false } = {}) => {
      if (!geste) return { etat: "absente" };
      // L'aperçu arrive APRÈS le montage : tant qu'il manque, il n'y a rien à
      // lire, et surtout rien à poser comme lu.
      if (manqueApercu) return { etat: "attente" };
      const p = phraseDeLaCarte({
        // ⚠️ LE LEVIER, PAS LES MARQUAGES. La garde vocale se décide au
        // REGISTRE : une carte d'événement arrive sans marquages, et ceux du
        // geste sont d'affichage, pas d'autorisation (revue #1482).
        levier: geste.levier,
        titre: geste.titre,
        // ⚠️ LE NOM, JAMAIS L'IDENTIFIANT. L'écran retombe sur `lc1` quand le
        // nom manque (voir le rendu de l'aperçu plus bas) ; la voix, elle,
        // dit qu'elle ne sait pas nommer.
        nomLogement:
          (apercu.data && apercu.data.logement && apercu.data.logement.nom) ||
          null,
        aUnLogement: !!(apercu.data && apercu.data.logement),
        libelle: blocLu && blocLu.libelle,
        // ⚠️ SUR QUOI PORTE LE GESTE, quand la lecture le sait : les lectures
        // de période rendent `periode` (« le 27 septembre »). C'est ce que la
        // VOIX prend pour sujet — l'écran, lui, montre déjà tout le reste.
        quand: (apercu.data && apercu.data.periode) || null,
        actuelLisible: blocLu && blocLu.actuel_lisible,
        aUneSuite,
        nouvelleLisible:
          aUneSuite && valeurSuivante !== undefined
            ? valeurDite(
                (apercuDecl && apercuDecl.valeur_arg) || "nouvelle",
                valeurSuivante,
              )
            : null,
        unite: blocLu && blocLu.unite,
        bloque,
        raisonBlocage: bloque ? raisonDuBlocage() : "",
        prose,
        vanneOuverte,
      });
      // `nomLogement` remonte aussi : quand DEUX cartes sont prêtes, il les
      // nomme par leur titre ET leur logement (deux prix se ressembleraient).
      const nomLogement =
        (apercu.data && apercu.data.logement && apercu.data.logement.nom) ||
        null;
      return p
        ? { etat: "prete", nomLogement, ...p }
        : { etat: "absente", nomLogement };
    };
  });

  if (!geste) return null;

  // ══ APRÈS LE CLIC, LA CARTE SE REPLIE SUR SON RÉSULTAT ════════════════════
  //
  // Dimitri, 2026-09-15 : « Le texte de la carte est affiché avant mon clic,
  // puis intégralement répété après. Je le lis deux fois pour rien. Après le
  // geste, il dit ce qui s'est passé. Pas ce qu'il allait faire. »
  //
  // Tout ce qui sert à DÉCIDER — les arguments, l'aperçu, les avertissements,
  // « après votre clic : … » — a fait son travail au moment du clic. Le garder
  // ensuite, c'est faire relire une promesse au passé.
  //
  // ⚠️ ON NE REPLIE QUE CE QUI A ABOUTI. Un geste REFUSÉ propose « Réessayer » :
  // replier cacherait précisément ce qu'il faut corriger avant de recliquer (le
  // motif, le texte, le choix).
  //
  // ⚠️ ET UN GESTE INCERTAIN NON PLUS (corrigé le 2026-09-15, revue adverse
  // #1435, MAJEUR 4). Il se repliait — le bouton est mort, il n'y a plus rien à
  // refaire ICI. Mais « le geste a été tenté, on ne sait pas s'il a abouti » est
  // exactement le moment où Dimitri doit aller VÉRIFIER : sur quel logement, sur
  // quelles dates, avec quelle valeur. Replier lui retirait la seule page où ces
  // arguments sont écrits — le tour de fil, lui, ne porte qu'une empreinte. La
  // carte reste donc entière, « après votre clic : … » compris : cette phrase
  // devient la consigne de vérification.
  const servi = etat === "fait";

  return (
    <div
      data-testid="operateur-geste-demande"
      data-levier={geste.levier}
      data-etat={etat}
      style={{
        marginTop: 10,
        border: `1px solid ${BORD}`,
        borderRadius: 12,
        background: "#fff",
        overflow: "hidden",
      }}
    >
      <div style={{ padding: "10px 12px 11px" }}>
        <div style={{ fontSize: 12.5, fontWeight: 700, color: DS.textPrimary }}>
          {geste.titre}
        </div>

        {/* ⚠️ CE QUI MANQUE, DIT EN PREMIER (revue adverse #1440) : avant les
            arguments, avant l'aperçu — un « Après votre clic : les dates
            prennent le prix demandé » sous un geste sans prix se lirait comme
            une promesse. */}
        {!servi && manqueIndisp && (
          <p
            data-testid="geste-demande-indispensable"
            style={{
              margin: "8px 0 0",
              fontSize: 11.5,
              color: "#8f2f1d",
              fontWeight: 600,
            }}
          >
            ⛔ {manqueIndisp.phrase} — ce geste ne partira pas.
          </p>
        )}

        {!servi && lignesArgs.length > 0 && (
          <dl
            data-testid="geste-demande-args"
            style={{
              margin: "8px 0 0",
              display: "grid",
              gridTemplateColumns: "auto 1fr",
              gap: "3px 10px",
              fontSize: 11.5,
            }}
          >
            {lignesArgs.map((ligne) => (
              <div key={ligne.cle} style={{ display: "contents" }}>
                <dt style={{ color: "#5c6773" }}>{ligne.libelle}</dt>
                <dd style={{ margin: 0, fontWeight: 600 }}>{ligne.valeur}</dd>
              </div>
            ))}
          </dl>
        )}

        {/* ── L'APERÇU : LOGEMENT · CHAMP EN FRANÇAIS · ANCIENNE → NOUVELLE ──
            Lu par la carte, jamais composé par le modèle. Tant qu'il n'est
            pas là, le bouton est gris : on ne clique pas sur un « → Y » dont
            on ne connaît pas le X. */}
        {!servi && apercuDecl && (
          <div data-testid="geste-demande-apercu" data-etat={apercu.etat}>
            {apercu.etat === "chargement" && (
              <p
                style={{ margin: "8px 0 0", fontSize: 11.5, color: "#5c6773" }}
              >
                Lecture de la valeur actuelle…
              </p>
            )}
            {apercu.etat === "echec" && (
              <p
                data-testid="geste-demande-apercu-echec"
                style={{ margin: "8px 0 0", fontSize: 11.5, color: "#b91c1c" }}
              >
                La valeur actuelle n'a pas pu être lue ({apercu.erreur}). Rien
                n'a été fait, et ce geste ne partira pas sans elle.
              </p>
            )}
            {champAbsentDeLaLecture && (
              <p
                data-testid="geste-demande-apercu-champ-absent"
                style={{ margin: "8px 0 0", fontSize: 11.5, color: "#b91c1c" }}
              >
                La valeur actuelle n'est pas dans ce que j'ai lu. Rien n'a été
                fait, et ce geste ne partira pas sans elle.
              </p>
            )}
            {apercu.etat === "pret" && blocLu && (
              <div
                style={{
                  margin: "8px 0 0",
                  padding: "8px 10px",
                  borderRadius: 8,
                  background: "#f6f8fa",
                  fontSize: 11.5,
                }}
              >
                <div style={{ fontWeight: 700, color: DS.textPrimary }}>
                  {/* Une lecture sans `logement` (politiques d'annulation,
                      communes) porte un libellé autosuffisant : pas de tiret
                      orphelin devant.
                      ⚠️ ET PLUS DE REPLI SUR L'IDENTIFIANT (revue #1487) : cet
                      en-tête affichait « lc2 — le prix de la période » sur la
                      carte la plus utilisée du chemin argent, parce que sept
                      lectures rendaient `logement: { id }` sans nom. Elles le
                      rendent maintenant ; et si une lecture ne sait pas
                      nommer, on l'écrit — un code affiché se lit comme une
                      donnée, un « (logement) » se remarque et se corrige. */}
                  {apercu.data.logement
                    ? `${apercu.data.logement.nom || "(logement)"} — `
                    : ""}
                  {blocLu.libelle}
                </div>
                {/* ⚠️ « ACTUELLEMENT » DISPARAÎT QUAND IL N'Y A RIEN (2026-09-15).
                    « actuellement aucun prix de base enregistré » se lit mal ;
                    Dimitri demande « aucun prix de base enregistré → 75 € ».
                    Et jamais « 0 € → 75 € » : un zéro serait un montant. */}
                <div
                  data-testid="geste-demande-apercu-valeurs"
                  style={{ marginTop: 3 }}
                >
                  {!apercuDecl.champ
                    ? "actuellement "
                    : blocLu.actuel === null || blocLu.actuel === undefined
                      ? null
                      : `${blocLu.libelle} : `}
                  <strong>{blocLu.actuel_lisible ?? "(vide)"}</strong>
                  {/* ⚠️ PAS DE FLÈCHE QUAND IL N'Y A PAS D'APRÈS (2026-09-16).
                      Trois gestes n'ont aucune valeur neuve à annoncer :
                      retirer les punaises (le prix ne bouge pas au clic ; ce
                      qui l'écrira ensuite — relecture Beds24, prix de base —
                      n'est pas décidé), envoyer la file (elle part telle quelle),
                      relire chez Beds24 (on ne sait qu'après). Et « deux nuits
                      minimum du 20 au 31 » ne porte aucun prix. Une flèche
                      vers « — » se lit comme une valeur effacée : on montre
                      alors l'état, et c'est tout. */}
                  {aUneSuite ? (
                    <>
                      {" → "}
                      <strong>
                        {valeurDite(
                          (apercuDecl && apercuDecl.valeur_arg) || "nouvelle",
                          valeurSuivante,
                        )}
                        {blocLu.unite ? ` ${blocLu.unite}` : ""}
                      </strong>
                    </>
                  ) : null}
                </div>
                <div style={{ marginTop: 3, color: "#5c6773" }}>
                  À constater : {blocLu.ou}
                </div>
                {refusLu && (
                  <p
                    data-testid="geste-demande-apercu-refus"
                    style={{
                      margin: "6px 0 0",
                      color: "#8f2f1d",
                      fontWeight: 600,
                    }}
                  >
                    ⛔ {refusLu}. Ce geste ne partira pas.
                  </p>
                )}
                {horsListe && (
                  <p
                    data-testid="geste-demande-apercu-hors-liste"
                    style={{
                      margin: "6px 0 0",
                      color: "#b91c1c",
                      fontWeight: 600,
                    }}
                  >
                    Valeur hors liste — attendu : {horsListe.join(" | ")}. Ce
                    geste ne partira pas.
                  </p>
                )}
                {gelLu && gelLu.gele === true && (
                  <p
                    data-testid="geste-demande-apercu-gele"
                    style={{
                      margin: "6px 0 0",
                      color: "#8f2f1d",
                      fontWeight: 600,
                    }}
                  >
                    ⛔ {gelLu.label} est déjà facturé pour ce logement — ce
                    champ ne se modifie plus pour ce mois. Ce geste ne partira
                    pas.
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── LE TEXTE QUE DIMITRI RELIT ET CORRIGE ──────────────────────
            Pré-rempli avec la proposition ; ce qui part est ce qui est LÀ au
            moment du clic. Une fois parti, il reste affiché tel qu'envoyé.

            ⚠️ SEUL BLOC QUI SURVIT AU REPLI (2026-09-15), et c'est délibéré :
            le corps d'un message n'est pas « ce que le geste allait faire »,
            c'est CE QUI EST PARTI chez un voyageur. Le cacher après l'envoi
            retirerait la seule trace de ce qui a été écrit en son nom. Le champ
            est déjà figé (`disabled`) une fois le geste parti. */}
        {clesTextes.map((cle) => {
          const v = String(textes[cle] || "");
          const vide = !v.trim();
          const tropLong = v.length > MAX_CARS_TEXTE;
          return (
            <div key={cle} data-testid={`geste-demande-texte-${cle}`}>
              <label
                style={{
                  display: "block",
                  marginTop: 8,
                  fontSize: 11.5,
                  color: "#5c6773",
                }}
              >
                {cle} — relisez, corrigez si besoin : c'est ce texte qui
                partira.
                <textarea
                  data-testid={`geste-demande-texte-${cle}-champ`}
                  value={v}
                  disabled={etat === "envoi" || etat === "fait"}
                  onChange={(e) => {
                    const t = e.target.value;
                    setTextes((x) => ({ ...x, [cle]: t }));
                  }}
                  rows={Math.min(10, Math.max(3, v.split("\n").length + 1))}
                  style={{
                    display: "block",
                    marginTop: 4,
                    width: "100%",
                    padding: "6px 9px",
                    border: `1px solid ${vide || tropLong ? "#b91c1c" : BORD}`,
                    borderRadius: 8,
                    fontSize: 12,
                    fontFamily: "inherit",
                    lineHeight: 1.4,
                    boxSizing: "border-box",
                    resize: "vertical",
                    background: etat === "fait" ? "#f6f8fa" : "#fff",
                  }}
                />
              </label>
              {etat !== "fait" && (vide || tropLong) && (
                <p
                  data-testid={`geste-demande-texte-${cle}-bloque`}
                  style={{
                    margin: "4px 0 0",
                    fontSize: 11.5,
                    color: "#b91c1c",
                    fontWeight: 600,
                  }}
                >
                  {vide
                    ? "Le texte est vide — rien ne partira tant qu'il l'est."
                    : `Le texte dépasse ${MAX_CARS_TEXTE} caractères (${v.length}) — raccourcissez-le.`}
                </p>
              )}
            </div>
          );
        })}

        {(servi ? [] : marquages).map((m) =>
          AVERTISSEMENTS[m] ? (
            <p
              key={m}
              data-testid={`geste-demande-marquage-${m}`}
              style={{
                margin: "8px 0 0",
                fontSize: 11.5,
                color: m === "sortant" ? DS.terra : "#b45309",
                fontWeight: 600,
              }}
            >
              {AVERTISSEMENTS[m]}
            </p>
          ) : null,
        )}

        {/* ── CE QUE ÇA FAIT — OÙ ÇA PART, CE QUE ÇA COÛTE, CE QU'IL NE FAIT
            PAS (2026-09-15) ─────────────────────────────────────────────
            Dimitri, à propos du séjour minimum (qui part chez Beds24) et des
            codes d'accès (qui atteignent le voyageur par le livret) : « garde
            -les ouverts, mais que la carte le dise clairement. »

            ⚠️ LES PHRASES VIENNENT DU SERVEUR, GLOSÉES DEPUIS LE REGISTRE. La
            carte n'a aucune table de destinations à elle : elle en aurait une
            périmée au premier jeton ajouté. Une destination qui SORT de chez
            FBZ est mise en évidence — c'est ce que Dimitri veut voir. */}
        {!servi && (effets.ou.length || effets.cout.length || g.ne_fait_pas) ? (
          <div
            data-testid="geste-demande-effets"
            style={{
              margin: "8px 0 0",
              padding: "8px 10px",
              borderRadius: 8,
              background: "#f6f8fa",
              fontSize: 11.5,
              lineHeight: 1.45,
            }}
          >
            <div style={{ fontWeight: 700, color: DS.textPrimary }}>
              Ce que ça fait
            </div>
            {effets.ou.length ? (
              <div
                data-testid="geste-demande-effets-ou"
                style={{ marginTop: 3 }}
              >
                <span style={{ color: "#5c6773" }}>Où ça part : </span>
                {effets.ou.map((o, i) => (
                  <span
                    key={o.cle}
                    data-testid={`geste-demande-effets-ou-${o.cle}`}
                    data-externe={o.externe ? "oui" : "non"}
                    style={{
                      color: o.externe ? DS.terra : "#5c6773",
                      fontWeight: o.externe ? 700 : 400,
                    }}
                  >
                    {i ? " · " : ""}
                    {o.texte}
                  </span>
                ))}
              </div>
            ) : null}
            {effets.cout.length ? (
              <div
                data-testid="geste-demande-effets-cout"
                style={{ marginTop: 3, color: "#5c6773" }}
              >
                Ce que ça coûte : {effets.cout.map((c) => c.texte).join(" · ")}
              </div>
            ) : null}
            {g.ne_fait_pas ? (
              <div
                data-testid="geste-demande-effets-ne-fait-pas"
                style={{ marginTop: 3, color: "#5c6773" }}
              >
                Ce qu'il ne fait pas : {g.ne_fait_pas}
              </div>
            ) : null}
          </div>
        ) : null}

        {/* ── CE QUI NE REVIENDRA PAS ────────────────────────────────────
            Dimitri, 2026-09-13 : « corrige la carte qui ment sur la
            réversibilité : déverrouiller ne restaure pas l'ancien prix, elle
            doit le dire. »

            ⚠️ Cette phrase passe AVANT « après votre clic » — et c'est
            l'inverse de l'ordre naturel. « Voilà ce que vous verrez » se lit
            comme une promesse ; ce qui ne revient pas doit être lu AVANT, tant
            que le doigt n'est pas sur le bouton. */}
        {!servi && geste.sans_retour ? (
          <p
            data-testid="geste-demande-sans-retour"
            style={{
              margin: "8px 0 0",
              fontSize: 11.5,
              color: "#8f2f1d",
              fontWeight: 600,
            }}
          >
            ⚠️ Sans retour : {geste.sans_retour}
          </p>
        ) : null}

        {!servi && (
          <p
            data-testid="geste-demande-constatable"
            style={{ margin: "8px 0 0", fontSize: 11.5, color: "#5c6773" }}
          >
            Après votre clic : {geste.constatable}
            {/* « Dans Logements » veut dire « quelque part » : la fiche a dix
                onglets. Le libellé vient du serveur, jamais la clé. */}
            {g.onglet && g.onglet.libelle
              ? ` — à regarder dans l'onglet ${g.onglet.libelle}.`
              : null}
          </p>
        )}

        {vagueFermee && etat !== "fait" && (
          <p
            data-testid="geste-demande-vague-fermee"
            style={{
              margin: "8px 0 0",
              fontSize: 11.5,
              color: "#8f2f1d",
              fontWeight: 600,
            }}
          >
            ⛔ {vagueFermee.message}
          </p>
        )}

        {(servi ? [] : clesChoisies).map((cle) => {
          const l = listes[cle] || { etat: "chargement", options: [] };
          const etiquette = choisis[cle].etiquette || cle;
          const actuel = l.options && l.options.find((o) => o.actuel);
          return (
            <div key={cle} data-testid={`geste-demande-choix-${cle}`}>
              {l.etat === "chargement" && (
                <p
                  style={{
                    margin: "8px 0 0",
                    fontSize: 11.5,
                    color: "#5c6773",
                  }}
                >
                  {etiquette} : lecture de la liste…
                </p>
              )}
              {l.etat === "echec" && (
                <p
                  data-testid={`geste-demande-choix-${cle}-echec`}
                  style={{
                    margin: "8px 0 0",
                    fontSize: 11.5,
                    color: "#b91c1c",
                  }}
                >
                  {etiquette} : la liste n'a pas pu être lue ({l.erreur}). Rien
                  n'a été fait.
                </p>
              )}
              {l.etat === "pret" && l.options.length === 0 && (
                <p
                  data-testid={`geste-demande-choix-${cle}-vide`}
                  style={{
                    margin: "8px 0 0",
                    fontSize: 11.5,
                    color: "#b45309",
                  }}
                >
                  {etiquette} : personne n'est éligible — aucun compte n'a accès
                  à ce logement.
                </p>
              )}
              {l.etat === "pret" && l.options.length > 0 && etat !== "fait" && (
                <label
                  style={{
                    display: "block",
                    marginTop: 8,
                    fontSize: 11.5,
                    color: "#5c6773",
                  }}
                >
                  {etiquette}
                  <select
                    data-testid={`geste-demande-choix-${cle}-select`}
                    value={choix[cle] || ""}
                    onChange={(e) =>
                      setChoix((c) => ({ ...c, [cle]: e.target.value }))
                    }
                    style={{
                      display: "block",
                      marginTop: 4,
                      width: "100%",
                      minHeight: 30,
                      border: `1px solid ${BORD}`,
                      borderRadius: 8,
                      fontSize: 12,
                      fontFamily: "inherit",
                      background: "#fff",
                    }}
                  >
                    <option value="">— choisir —</option>
                    {l.options.map((o) => (
                      <option key={o.valeur} value={o.valeur}>
                        {o.libelle}
                        {o.actuel ? " (actuel)" : ""}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              {/* L'écrasement se lit AVANT le clic : quelqu'un porte déjà la
                  valeur, et le geste va le remplacer. */}
              {l.etat === "pret" && l.assigne && etat !== "fait" && (
                <p
                  data-testid={`geste-demande-choix-${cle}-remplace`}
                  style={{
                    margin: "6px 0 0",
                    fontSize: 11.5,
                    color: "#8f2f1d",
                    fontWeight: 600,
                  }}
                >
                  ⚠️ Déjà assigné{actuel ? ` à ${actuel.libelle}` : ""} — ce
                  geste le remplace.
                </p>
              )}
            </div>
          );
        })}

        {/* ── CE QUE DIMITRI TAPE — le prix payé d'un achat (revue #1501, M4) ──
            Jamais pré-rempli : le modèle ne le compose pas, et le serveur
            retire ce qu'il aurait composé. Le repère du catalogue, lu par
            l'aperçu, s'affiche à côté — c'est là que l'écart se voit. */}
        {etat !== "fait" &&
          saisiesDecl.map(([chemin, decl]) => {
            const v = saisies[chemin] == null ? "" : String(saisies[chemin]);
            const manque = valeurSaisie(v, decl.nature) === null;
            const repereCatalogue =
              blocLu &&
              blocLu.ce_qui_sera_ecrit &&
              typeof blocLu.ce_qui_sera_ecrit.montant_catalogue_eur === "number"
                ? blocLu.ce_qui_sera_ecrit.montant_catalogue_eur
                : null;
            return (
              <label
                key={chemin}
                data-testid={`geste-demande-saisie-${chemin}`}
                style={{
                  display: "block",
                  marginTop: 8,
                  fontSize: 11.5,
                  color: "#5c6773",
                }}
              >
                {decl.libelle.charAt(0).toUpperCase()}
                {decl.libelle.slice(1)} — à taper, je ne le devine pas
                {repereCatalogue !== null
                  ? ` (au catalogue : ${repereCatalogue.toFixed(2).replace(".", ",")} €)`
                  : ""}
                <input
                  data-testid={`geste-demande-saisie-${chemin}-champ`}
                  // Un montant signé se tape avec son moins : le clavier
                  // décimal des mobiles n'en a pas toujours.
                  inputMode={
                    decl.nature === "montant_signe" ? "text" : "decimal"
                  }
                  value={v}
                  disabled={etat === "envoi"}
                  onChange={(e) => {
                    const t = e.target.value;
                    setSaisies((x) => ({ ...x, [chemin]: t }));
                  }}
                  placeholder={decl.nature === "nombre" ? "0" : "0,00"}
                  style={{
                    display: "block",
                    marginTop: 4,
                    width: "100%",
                    minHeight: 30,
                    padding: "0 9px",
                    border: `1px solid ${manque ? "#b91c1c" : BORD}`,
                    borderRadius: 8,
                    fontSize: 12,
                    fontFamily: "inherit",
                    boxSizing: "border-box",
                  }}
                />
                {avertissementDuSigne(geste && geste.levier, chemin, v) && (
                  <span
                    data-testid={`geste-demande-saisie-${chemin}-signe`}
                    style={{
                      display: "block",
                      marginTop: 4,
                      fontSize: 11.5,
                      color: "#b45309",
                      fontWeight: 600,
                    }}
                  >
                    {avertissementDuSigne(geste && geste.levier, chemin, v)}
                  </span>
                )}
              </label>
            );
          })}

        {geste.motif_requis && etat !== "fait" && (
          <input
            data-testid="geste-demande-motif"
            value={motif}
            onChange={(e) => setMotif(e.target.value)}
            placeholder="Votre motif (obligatoire)"
            style={{
              marginTop: 8,
              width: "100%",
              minHeight: 30,
              padding: "0 9px",
              border: `1px solid ${BORD}`,
              borderRadius: 8,
              fontSize: 12,
              fontFamily: "inherit",
              boxSizing: "border-box",
            }}
          />
        )}

        {/* ⚠️ PLUS DE `constatable` ICI (2026-09-15). La ligne disait
            « ✓ C'est fait. » suivie de la phrase « ce que vous verrez après » —
            la MÊME que celle affichée avant le clic, au futur, pour un geste
            désormais passé. Ce qui s'est passé, c'est le résumé juste en
            dessous ; ce que le geste ALLAIT faire ne s'écrit plus deux fois. */}
        {/* ⚠️ LA CASE QUI PORTE LA PREMIÈRE DES TROIS GARDES (2026-09-17).
            Elle n'est pas une politesse : c'est elle qui devient `confirm:
            true` côté serveur. Elle redit ce qui ne se rattrape pas — ces
            nuits repartent à la vente et peuvent être prises tout de suite —
            parce qu'une case cochée sans phrase ne confirme rien. */}
        {geste.confirmation_au_clic === true && etat !== "fait" && (
          <label
            data-testid="geste-demande-confirmation"
            style={{
              marginTop: 8,
              display: "flex",
              gap: 8,
              alignItems: "flex-start",
              fontSize: 12,
              lineHeight: 1.35,
              cursor: "pointer",
            }}
          >
            <input
              type="checkbox"
              checked={confirme}
              onChange={(e) => setConfirme(e.target.checked)}
              style={{ marginTop: 2 }}
            />
            <span>
              {geste.sans_retour
                ? `Je confirme. ${geste.sans_retour.charAt(0).toUpperCase()}${geste.sans_retour.slice(1)}.`
                : "Je confirme ce geste."}
            </span>
          </label>
        )}

        {etat === "fait" && (
          <p
            data-testid="geste-demande-fait"
            style={{ margin: "9px 0 0", fontSize: 12, fontWeight: 600 }}
          >
            ✓ C&rsquo;est fait.
          </p>
        )}

        {/* ⚠️ CE QUE LE GESTE A RAPPORTÉ — pour les gestes de LECTURE, c'est
            leur seul effet visible. Composé serveur, borné, jamais du JSON. */}
        {etat === "fait" && resume && (
          <p
            data-testid="geste-demande-resume"
            style={{
              margin: "6px 0 0",
              fontSize: 12,
              color: DS.texteSecondaire || "#5b6b7c",
              lineHeight: 1.45,
            }}
          >
            {resume}
          </p>
        )}

        {/* ⚠️ L'ÉCHEC DIT CE QUI S'EST PASSÉ, ET S'IL FAUT REFAIRE. La seule
            question qui compte après un échec est « est-ce parti ? » —
            `executerGeste` la tranche, et on ne propose « Réessayer » que
            lorsque la réponse est NON. */}
        {etat === "echec" && echec && (
          <p
            data-testid="geste-demande-echec"
            style={{ margin: "9px 0 0", fontSize: 12, color: "#b91c1c" }}
          >
            {echec.message}
          </p>
        )}

        {etat !== "fait" && (
          <button
            type="button"
            data-testid="geste-demande-bouton"
            className="fbz-press"
            disabled={
              etat === "envoi" ||
              bloque ||
              (echec && echec.etape === "levier_incertain")
            }
            onClick={executer}
            style={{
              marginTop: 10,
              minHeight: 32,
              padding: "0 14px",
              borderRadius: 16,
              background: bloque ? "#c8d2dd" : DS.ciel,
              border: "none",
              color: "#fff",
              fontSize: 12,
              fontWeight: 700,
              cursor: bloque ? "not-allowed" : "pointer",
              fontFamily: "inherit",
            }}
          >
            {etat === "envoi"
              ? "…"
              : etat === "echec"
                ? echec && echec.etape === "levier_incertain"
                  ? "Vérifiez avant de refaire"
                  : "Réessayer"
                : "Le faire"}
          </button>
        )}
      </div>
    </div>
  );
}

export default forwardRef(CarteGesteDemande);
